// src/components/LoanDataTable.jsx
import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import axios from 'axios';
import { saveAs } from 'file-saver';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './LoanDataTable.css';

const LoanDataTable = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [exportFormat, setExportFormat] = useState('excel');
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [columnDialogVisible, setColumnDialogVisible] = useState(false);
    const toast = useRef(null);
    const dt = useRef(null);

    const apiBaseUrl = process.env.REACT_APP_API_PATH || 'https://localhost:5001/api';

    const exportFormats = [
        { label: 'Excel', value: 'excel' },
        { label: 'CSV', value: 'csv' },
        { label: 'PDF', value: 'pdf' }
    ];

    // Define all available columns
    const allColumns = [
        { field: 'segmentIdentifier', header: 'Segment Identifier' },
        { field: 'memberIdentifier', header: 'Member Identifier' },
        { field: 'branchIdentifier', header: 'Branch Identifier' },
        { field: 'kendraCentreIdentifier', header: 'Kendra/Centre' },
        { field: 'groupIdentifier', header: 'Group Identifier' },
        { field: 'memberName1', header: 'Member Name' },
        { field: 'memberName2', header: 'Member Name 2' },
        { field: 'memberName3', header: 'Member Name 3' },
        { field: 'alternateNameOfMember', header: 'Alternate Name' },
        { field: 'memberBirthDate', header: 'Birth Date' },
        { field: 'memberAge', header: 'Age' },
        { field: 'memberSAgeAsOnDate', header: 'Age As On Date' },
        { field: 'memberGenderType', header: 'Gender' },
        { field: 'maritalStatusType', header: 'Marital Status' },
        { field: 'keyPersonSName', header: 'Key Person' },
        { field: 'keyPersonSRelationship', header: 'Key Person Relation' },
        { field: 'memberRelationshipName1', header: 'Relationship Name 1' },
        { field: 'memberRelationshipType1', header: 'Relationship Type 1' },
        { field: 'memberRelationshipName2', header: 'Relationship Name 2' },
        { field: 'memberRelationshipType2', header: 'Relationship Type 2' },
        { field: 'memberRelationshipName3', header: 'Relationship Name 3' },
        { field: 'memberRelationshipType3', header: 'Relationship Type 3' },
        { field: 'memberRelationshipName4', header: 'Relationship Name 4' },
        { field: 'memberRelationshipType4', header: 'Relationship Type 4' },
        { field: 'nomineeName', header: 'Nominee Name' },
        { field: 'nomineeRelationship', header: 'Nominee Relationship' },
        { field: 'nomineeAge', header: 'Nominee Age' },
        { field: 'voterSId', header: 'Voter ID' },
        { field: 'uid', header: 'UID' },
        { field: 'pan', header: 'PAN' },
        { field: 'rationCard', header: 'Ration Card' },
        { field: 'memberOtherId1Type', header: 'Other ID 1 Type' },
        { field: 'memberOtherId1', header: 'Other ID 1' },
        { field: 'memberOtherId2Type', header: 'Other ID 2 Type' },
        { field: 'memberOtherId2', header: 'Other ID 2' },
        { field: 'otherId3Type', header: 'Other ID 3 Type' },
        { field: 'otherId3Value', header: 'Other ID 3 Value' },
        { field: 'telephoneNumber1TypeIndicator', header: 'Tel Type 1' },
        { field: 'telephoneNumber1', header: 'Mobile No' },
        { field: 'telephoneNumber2TypeIndicator', header: 'Tel Type 2' },
        { field: 'telephoneNumber2', header: 'Alternate Mobile' },
        { field: 'memberSEducationalQualification', header: 'Education' },
        { field: 'assetOwnershipIndicator', header: 'Asset Ownership' },
        { field: 'numberOfDependents', header: 'Dependents' },
        { field: 'bankAccountBankName', header: 'Bank Name' },
        { field: 'bankAccountBranchName', header: 'IFSC' },
        { field: 'bankAccountAccountNumber', header: 'Account No' },
        { field: 'occupation', header: 'Occupation' },
        { field: 'totalMonthlyFamilyIncome', header: 'Monthly Income' },
        { field: 'monthlyFamilyExpenses', header: 'Monthly Expenses' },
        { field: 'memberSReligion', header: 'Religion' },
        { field: 'memberSCaste', header: 'Caste' },
        { field: 'groupLeaderIndicator', header: 'Group Leader' },
        { field: 'centerLeaderIndicator', header: 'Center Leader' },
        { field: 'memberSStatus', header: 'Member Status' },
        { field: 'segmentIdentifier1', header: 'Segment ID 1' },
        { field: 'memberSPermanentAddress', header: 'Permanent Address' },
        { field: 'stateCodePermanentAddress', header: 'Permanent State' },
        { field: 'pinCodePermanentAddress', header: 'Permanent Pincode' },
        { field: 'memberSCurrentAddress', header: 'Current Address' },
        { field: 'stateCodeCurrentAddress', header: 'Current State' },
        { field: 'pinCodeCurrentAddress', header: 'Current Pincode' },
        { field: 'dummy1', header: 'Dummy 1' },
        { field: 'segmentIdentifier2', header: 'Segment ID 2' },
        { field: 'uniqueAccountReferenceNumber', header: 'Account Reference' },
        { field: 'accountNumber', header: 'Account Number' },
        { field: 'branchIdentifier1', header: 'Branch ID 1' },
        { field: 'kendraCentreIdentifier1', header: 'Kendra/Centre 1' },
        { field: 'loanOfficer', header: 'Loan Officer' },
        { field: 'dateOfAccountInformation', header: 'Date of Account Info' },
        { field: 'loanCategory', header: 'Loan Category' },
        { field: 'groupIdentifier1', header: 'Group ID 1' },
        { field: 'loanCycleId', header: 'Loan Cycle' },
        { field: 'loanPurpose', header: 'Loan Purpose' },
        { field: 'accountStatus', header: 'Account Status' },
        { field: 'applicationDate', header: 'Application Date' },
        { field: 'sanctionedDate', header: 'Sanctioned Date' },
        { field: 'dateOpenedDisbursed', header: 'Disbursement Date' },
        { field: 'dateClosed', header: 'Closed Date' },
        { field: 'dateOfLastPayment', header: 'Last Payment Date' },
        { field: 'appliedForAmount', header: 'Applied Amount' },
        { field: 'loanAmountSanctioned', header: 'Sanctioned Amount' },
        { field: 'totalAmountDisbursed', header: 'Disbursed Amount' },
        { field: 'numberOfInstallments', header: 'Installments' },
        { field: 'repaymentFrequency', header: 'Repayment Frequency' },
        { field: 'minimumAmtDueInstalmentAmount', header: 'Installment Amount' },
        { field: 'currentBalance', header: 'Current Balance' },
        { field: 'amountOverdue', header: 'Amount Overdue' },
        { field: 'dpd', header: 'DPD (Days)' },
        { field: 'writeOffAmount', header: 'Write Off Amount' },
        { field: 'dateWriteOff', header: 'Write Off Date' },
        { field: 'writeOffReason', header: 'Write Off Reason' },
        { field: 'noOfMeetingsHeld', header: 'Meetings Held' },
        { field: 'noOfMeetingsMissed', header: 'Meetings Missed' },
        { field: 'insuranceIndicator', header: 'Insurance' },
        { field: 'typeOfInsurance', header: 'Insurance Type' },
        { field: 'sumAssuredCoverage', header: 'Sum Assured' },
        { field: 'agreedMeetingDayOfTheWeek', header: 'Meeting Day' },
        { field: 'agreedMeetingTimeOfTheDay', header: 'Meeting Time' },
        { field: 'dummy2', header: 'Dummy 2' }
    ];

    // Default visible columns (most important ones)
    const defaultColumns = [
        'segmentIdentifier',
        'memberIdentifier',
        'memberName1',
        'memberAge',
        'memberGenderType',
        'maritalStatusType',
        'accountStatus',
        'currentBalance',
        'amountOverdue',
        'dpd',
        'appliedForAmount',
        'numberOfInstallments',
        'dateOfLastPayment'
    ];

    // Initialize selected columns
    useEffect(() => {
        const initialSelected = allColumns.filter(col => defaultColumns.includes(col.field));
        setSelectedColumns(initialSelected);
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiBaseUrl}loandata`);
            setData(response.data);
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: `Loaded ${response.data.length} records successfully`,
                life: 3000
            });
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load data. Please try again.',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Export using the backend method
    const exportData = async () => {
        try {
            const response = await axios.get(`${apiBaseUrl}loandata/export`, {
                params: { export: exportFormat },
                responseType: 'blob'
            });

            const extension = exportFormat === 'excel' ? 'xls' : 
                             exportFormat === 'csv' ? 'csv' : 'pdf';
            const contentType = exportFormat === 'excel' ? 
                'application/vnd.ms-excel' :
                exportFormat === 'csv' ? 'text/csv' : 'application/pdf';

            const blob = new Blob([response.data], { type: contentType });
            saveAs(blob, `LoanData_${new Date().toISOString().slice(0,10)}.${extension}`);
            
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: `File exported successfully as ${exportFormat.toUpperCase()}`,
                life: 3000
            });
        } catch (error) {
            console.error('Error exporting data:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: `Failed to export as ${exportFormat.toUpperCase()}. Please try again.`,
                life: 5000
            });
        }
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const toggleColumn = (col) => {
        const isSelected = selectedColumns.some(c => c.field === col.field);
        if (isSelected) {
            setSelectedColumns(selectedColumns.filter(c => c.field !== col.field));
        } else {
            setSelectedColumns([...selectedColumns, col]);
        }
    };

    const selectAllColumns = () => {
        setSelectedColumns([...allColumns]);
    };

    const deselectAllColumns = () => {
        setSelectedColumns([]);
    };

    const renderHeader = () => {
        return (
            <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="flex gap-2 align-items-center flex-wrap">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={onGlobalFilterChange}
                            placeholder="Global Search"
                            className="p-inputtext-sm"
                            style={{ width: '250px' }}
                        />
                    </span>
                    <span className="p-float-label">
                        <Dropdown
                            value={exportFormat}
                            options={exportFormats}
                            onChange={(e) => setExportFormat(e.value)}
                            className="p-inputtext-sm"
                            style={{ width: '120px' }}
                        />
                        <label>Format</label>
                    </span>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button
                        label="Columns"
                        icon="pi pi-columns"
                        className="p-button-outlined p-button-sm"
                        onClick={() => setColumnDialogVisible(true)}
                    />
                    <Button
                        label="Export"
                        icon="pi pi-download"
                        className="p-button-success p-button-sm"
                        onClick={exportData}
                    />
                    <Button
                        label="Refresh"
                        icon="pi pi-refresh"
                        className="p-button-outlined p-button-sm"
                        onClick={fetchData}
                    />
                </div>
            </div>
        );
    };

    // Number formatting for currency columns
    const currencyBodyTemplate = (rowData, field) => {
        const value = rowData[field];
        if (value === null || value === undefined) return '';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Custom column rendering
    const columnBodyTemplate = (rowData, column) => {
        const field = column.field;
        const value = rowData[field];
        
        // Check if it's a currency field
        if (['currentBalance', 'amountOverdue', 'appliedForAmount', 
             'loanAmountSanctioned', 'totalAmountDisbursed', 'writeOffAmount',
             'sumAssuredCoverage', 'minimumAmtDueInstalmentAmount',
             'totalMonthlyFamilyIncome', 'monthlyFamilyExpenses'].includes(field)) {
            return currencyBodyTemplate(rowData, field);
        }
        
        // Check if it's a date field
        if (['memberBirthDate', 'dateOfLastPayment', 'applicationDate', 
             'sanctionedDate', 'dateOpenedDisbursed', 'dateClosed',
             'dateWriteOff', 'dateOfAccountInformation'].includes(field)) {
            return value ? value.trim() : '';
        }
        
        return value || '';
    };

    const header = renderHeader();

    return (
        <div className="loan-data-table">
            <Toast ref={toast} />
            <div className="card">
                <h2>Credit Status Upoad</h2>
                <div className="stats-bar">
                    <span className="stats-item">Total Records: {data.length}</span>
                    <span className="stats-item">Columns: {selectedColumns.length}</span>
                    <span className="stats-item">Last Updated: {new Date().toLocaleString()}</span>
                </div>
                <Toolbar className="mb-4" left={header} />
                
                {loading ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : (
                    <DataTable
                        ref={dt}
                        value={data}
                        paginator
                        rows={20}
                        rowsPerPageOptions={[10, 20, 50, 100, 200]}
                        filters={filters}
                        globalFilterFields={selectedColumns.map(col => col.field)}
                        emptyMessage="No data found"
                        className="p-datatable-sm"
                        sortable
                        showGridlines
                        stripedRows
                        responsiveLayout="scroll"
                        scrollable
                        scrollHeight="600px"
                        virtualScroll
                        lazy
                        totalRecords={data.length}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    >
                        {selectedColumns.map((col) => (
                            <Column
                                key={col.field}
                                field={col.field}
                                header={col.header}
                                sortable
                                filter
                                filterMatchMode={FilterMatchMode.CONTAINS}
                                style={{ minWidth: '150px' }}
                                body={(rowData) => columnBodyTemplate(rowData, col)}
                            />
                        ))}
                    </DataTable>
                )}
            </div>

            {/* Column Selection Dialog */}
            <Dialog
                header="Select Columns"
                visible={columnDialogVisible}
                style={{ width: '600px' }}
                onHide={() => setColumnDialogVisible(false)}
                footer={
                    <div>
                        <Button label="Select All" icon="pi pi-check" onClick={selectAllColumns} className="p-button-text" />
                        <Button label="Deselect All" icon="pi pi-times" onClick={deselectAllColumns} className="p-button-text" />
                        <Button label="Close" icon="pi pi-times" onClick={() => setColumnDialogVisible(false)} className="p-button-primary" />
                    </div>
                }
            >
                <div className="column-selector">
                    {allColumns.map((col) => (
                        <div key={col.field} className="column-checkbox">
                            <input
                                type="checkbox"
                                id={`col-${col.field}`}
                                checked={selectedColumns.some(c => c.field === col.field)}
                                onChange={() => toggleColumn(col)}
                            />
                            <label htmlFor={`col-${col.field}`}>{col.header}</label>
                        </div>
                    ))}
                </div>
            </Dialog>
        </div>
    );
};

export default LoanDataTable;